/**
 * Professional Bully (291) — fist VFX + HP ghost sync helpers.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * BULLY ANIMATION GUARDRAILS (read before changing any bully-related code)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Contract: exactly ONE fist + punch sound per immobilize event that deals bully damage.
 *
 * Three coordinated pieces (all required — removing one re-breaks the others):
 *
 * 1. SERVER (Immobilize.js → bullyDamageInstanceIds, clearEphemeralBullyDamageAnnotation)
 *    - Push target instanceId when bully damage lands (one-shot ephemeral annotation).
 *    - clearEphemeralBullyDamageAnnotation MUST run after EVERY sendGameStateToPlayers.
 *      Clearing only on strategem-complete → stale ids rebroadcast on turn-pass → duplicate fists.
 *
 * 2. CLIENT WS ENTRY (BattleUI tryConsumeBullyDamageInstanceIdsAtWsEntry — MUST stay wired)
 *    - Consumes bullyDamageInstanceIds BEFORE stateVersion guard (0.2.60 perf architecture).
 *    - Deletes from inbound fragment so ids never persist in React state.
 *    - Handles game-update / turn-played / deploy paths.
 *    - Skips strategem-complete & attack-complete (those have dedicated handlers below).
 *    - Do NOT re-add an inline game-update handler (wrong prevState timing, double-fire risk).
 *
 * 3. CLIENT DEDUP (consumedBullyBroadcastKeysRef + buildBully*BroadcastKey)
 *    - game-update/turn-played keys use stateVersion (NOT turnNumber — blocks same-turn re-immobilize).
 *    - strategem-complete keys use strategemInstanceId; attack-complete uses attackId.
 *    - stateVersion dedup is safe ONLY with server clear (1) — never revert to turn-only keys.
 *
 * Dedicated handlers (do not route these through WS entry consumer):
 *    - strategem-complete: deferFistPlayback until strategem projectile finishes.
 *    - attack-complete / attack-complete-minimal ephemeralVfx: resolveWsAttackVfxFragment.
 *
 * 4. CLIENT IMPACT SYNC (scheduleBullyFistPlayback)
 *    - Launch fist at resolveBullyFistLaunchDelayMs(i).
 *    - Apply HP reveal / death / punch sound only at resolveBullyFistImpactDelayMs(i)
 *      (= launch + travel). Applying on launch makes units die ~travelMs early.
 *
 * 5. CLIENT LETHAL SUPPRESSION (while bullyDamagePendingInstanceIds has the unit)
 *    - Slot-death / strategem broadcast lethal FX must not run early for pending targets.
 *    - Early lethal fade + damage pop causes: indicators → disappear → bully ghost reappear.
 *    - Pending set stores String(instanceId); always check via isBullyDamagePendingInstanceId.
 *
 * Regression tests: __tests__/regression/Bully*.js, BullyDamageDisplaySync.twins.test.js
 * ═══════════════════════════════════════════════════════════════════════════════
 */

/** Default fist VFX timing (ms). Keep in sync with BattleUI BULLY_* constants. */
export const BULLY_FIST_DEFAULT_TIMING_MS = Object.freeze({
  baseDelayMs: 200,
  punchIntervalMs: 400,
  travelMs: 200,
});

/** Each broadcast ships a fresh bullyDamageInstanceIds list — animate the full set once. */
export function resolveBullyInstanceIdsForAnimation(allIds) {
  if (!Array.isArray(allIds) || allIds.length === 0) {
    return [];
  }
  return [...allIds];
}

/**
 * Delay before launching fist i (0-based). Damage/death must NOT apply here.
 */
export function resolveBullyFistLaunchDelayMs(punchIndex, timing = BULLY_FIST_DEFAULT_TIMING_MS) {
  if (!Number.isInteger(punchIndex) || punchIndex < 0) {
    throw new Error('resolveBullyFistLaunchDelayMs: punchIndex must be a non-negative integer');
  }
  const baseDelayMs = timing?.baseDelayMs;
  const punchIntervalMs = timing?.punchIntervalMs;
  if (typeof baseDelayMs !== 'number' || typeof punchIntervalMs !== 'number') {
    throw new Error('resolveBullyFistLaunchDelayMs: timing.baseDelayMs and timing.punchIntervalMs are required');
  }
  return baseDelayMs + punchIndex * punchIntervalMs;
}

/**
 * Delay before revealing damage/death for fist i — when the fist sprite reaches the target.
 */
export function resolveBullyFistImpactDelayMs(punchIndex, timing = BULLY_FIST_DEFAULT_TIMING_MS) {
  const launchMs = resolveBullyFistLaunchDelayMs(punchIndex, timing);
  const travelMs = timing?.travelMs;
  if (typeof travelMs !== 'number') {
    throw new Error('resolveBullyFistImpactDelayMs: timing.travelMs is required');
  }
  return launchMs + travelMs;
}

/** Total sequence length until the last impact (+ trailing buffer for flash/cleanup). */
export function resolveBullyFistSequenceResetMs(targetCount, timing = BULLY_FIST_DEFAULT_TIMING_MS, trailingBufferMs = 400) {
  if (!Number.isInteger(targetCount) || targetCount < 1) {
    throw new Error('resolveBullyFistSequenceResetMs: targetCount must be a positive integer');
  }
  if (typeof trailingBufferMs !== 'number') {
    throw new Error('resolveBullyFistSequenceResetMs: trailingBufferMs is required');
  }
  return resolveBullyFistImpactDelayMs(targetCount - 1, timing) + trailingBufferMs;
}

export function buildBullyBroadcastConsumptionKey(sourceType, sourceId, instanceIds) {
  if (!sourceType) {
    throw new Error('buildBullyBroadcastConsumptionKey: sourceType is required');
  }
  if (sourceId == null) {
    throw new Error('buildBullyBroadcastConsumptionKey: sourceId is required');
  }
  if (!Array.isArray(instanceIds) || instanceIds.length === 0) {
    throw new Error('buildBullyBroadcastConsumptionKey: instanceIds is required');
  }
  return `${sourceType}:${String(sourceId)}:${instanceIds.join(',')}`;
}

/** Dedup key for game-update / turn-played (client safety net).
 *  Uses stateVersion so a second immobilize of the same unit in the same turn gets a fresh key.
 *  Safe only because server clearEphemeralBullyDamageAnnotation prevents turn-pass rebroadcasts
 *  (stale ids + new stateVersion was the old duplicate-fist regression). */
export function buildBullyGameUpdateBroadcastKey(msgType, gameStateFragment, instanceIds) {
  if (!msgType) {
    throw new Error('buildBullyGameUpdateBroadcastKey: msgType is required');
  }
  if (!Array.isArray(instanceIds) || instanceIds.length === 0) {
    throw new Error('buildBullyGameUpdateBroadcastKey: instanceIds is required');
  }
  const broadcastAnchor =
    gameStateFragment?.stateVersion
    ?? gameStateFragment?.turnNumber
    ?? gameStateFragment?.turn
    ?? 'no-broadcast-id';
  return buildBullyBroadcastConsumptionKey(msgType, broadcastAnchor, instanceIds);
}

export function stripBullyDamageAnnotationFromGameState(gameState) {
  if (!gameState || typeof gameState !== 'object') {
    throw new Error('stripBullyDamageAnnotationFromGameState: gameState is required');
  }
  if (!('bullyDamageInstanceIds' in gameState)) {
    return gameState;
  }
  const next = { ...gameState };
  delete next.bullyDamageInstanceIds;
  return next;
}

export function filterHealthHoldTargetsExcludingBullyDamage(holdTargets, bullyDamageInstanceIds) {
  if (!Array.isArray(holdTargets) || holdTargets.length === 0) {
    return [];
  }
  if (!Array.isArray(bullyDamageInstanceIds) || bullyDamageInstanceIds.length === 0) {
    return holdTargets;
  }
  const bullySet = new Set(bullyDamageInstanceIds.map((id) => String(id)));
  return holdTargets.filter((target) => {
    if (target?.targetInstanceId == null) {
      return true;
    }
    return !bullySet.has(String(target.targetInstanceId));
  });
}

/** Pending fist targets — always keyed as String(instanceId). */
export function isBullyDamagePendingInstanceId(pendingSet, instanceId) {
  if (!pendingSet || instanceId == null) {
    return false;
  }
  return pendingSet.has(String(instanceId));
}

export function addBullyDamagePendingInstanceId(pendingSet, instanceId) {
  if (!pendingSet) {
    throw new Error('addBullyDamagePendingInstanceId: pendingSet is required');
  }
  if (instanceId == null) {
    throw new Error('addBullyDamagePendingInstanceId: instanceId is required');
  }
  pendingSet.add(String(instanceId));
}

export function removeBullyDamagePendingInstanceId(pendingSet, instanceId) {
  if (!pendingSet || instanceId == null) {
    return false;
  }
  return pendingSet.delete(String(instanceId));
}

export function hasActiveStrategemAnimationsForInstance(activeAnimations, strategemInstanceId) {
  if (strategemInstanceId == null || !Array.isArray(activeAnimations)) {
    return false;
  }
  const instanceKey = String(strategemInstanceId);
  return activeAnimations.some(
    (anim) => anim?.strategemInstanceId != null && String(anim.strategemInstanceId) === instanceKey
  );
}
